import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { checkFactusolStatus, syncFactusolProduct } from '../lib/api';
import { CheckCircle, XCircle, RefreshCw, Server, ArrowRight } from 'lucide-react';
import { useToast } from '@/components/ui/Toast';

export default function FactusolPage() {
    const toast = useToast();
    const [reference, setReference] = useState('');
    const [syncResult, setSyncResult] = useState<any>(null);

    // Status Query
    const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
        queryKey: ['factusolStatus'],
        queryFn: checkFactusolStatus,
    });

    // Sync Mutation
    const syncMutation = useMutation({
        mutationFn: syncFactusolProduct,
        onSuccess: (data) => {
            setSyncResult(data);
            toast.success('Producto sincronizado correctamente');
        },
        onError: (error: any) => {
            toast.error('Error sincronizando producto', error.response?.data?.detail || error.message);
        }
    });

    const handleSync = (e: React.FormEvent) => {
        e.preventDefault();
        if (!reference) return;
        setSyncResult(null);
        syncMutation.mutate(reference);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Integración Factusol</h1>
                <p className="mt-2 text-slate-600">
                    Gestiona la conexión y sincronización con Factusol (Software DELSOL).
                </p>
            </div>

            {/* Connection Status Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Server className="w-5 h-5" />
                        Estado de Conexión
                    </h2>
                    <button
                        onClick={() => refetchStatus()}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                        title="Verificar conexión"
                    >
                        <RefreshCw className={`w-5 h-5 ${statusLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    {statusLoading ? (
                        <span className="text-slate-500">Verificando...</span>
                    ) : status?.connected ? (
                        <div className="flex items-center gap-2 text-success-600 bg-green-50 px-4 py-2 rounded-lg">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Conectado correctamente</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-danger-600 bg-red-50 px-4 py-2 rounded-lg">
                            <XCircle className="w-5 h-5" />
                            <span className="font-medium">Error de conexión: {status?.error || 'Desconocido'}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Sync Product Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5" />
                    Sincronizar Producto
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                    Introduce la referencia del producto para actualizar su stock y precio desde Factusol.
                </p>

                <form onSubmit={handleSync} className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Referencia (REFART)
                        </label>
                        <input
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Ej: ACC-001"
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={syncMutation.isPending || !reference}
                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {syncMutation.isPending ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <ArrowRight className="w-4 h-4" />
                                Sincronizar
                            </>
                        )}
                    </button>
                </form>

                {/* Sync Result */}
                {syncResult && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-gray-200">
                        <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-success-600" />
                            Sincronización Completada
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="p-3 bg-white rounded border border-gray-100">
                                <span className="block text-slate-500 text-xs uppercase">Referencia</span>
                                <span className="font-mono font-medium">{syncResult.reference}</span>
                            </div>
                            <div className="p-3 bg-white rounded border border-gray-100">
                                <span className="block text-slate-500 text-xs uppercase">Precio Coste</span>
                                <span className="font-medium">{syncResult.synced_data.wholesale_price.toFixed(2)}€</span>
                            </div>
                            <div className="p-3 bg-white rounded border border-gray-100">
                                <span className="block text-slate-500 text-xs uppercase">PVP (con IVA)</span>
                                <span className="font-medium text-primary-600">{syncResult.synced_data.vat_price.toFixed(2)}€</span>
                            </div>
                            <div className="p-3 bg-white rounded border border-gray-100">
                                <span className="block text-slate-500 text-xs uppercase">Stock</span>
                                <span className="font-medium">{syncResult.synced_data.quantity} uds</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
